<script setup>
import { watch, ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, onBeforeRouteLeave } from 'vue-router';
import { useDestinationsStore } from '../../stores/destinations.store';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import DynamicField from '../../components/DynamicField.vue';
import PageBreadcrumb from '../../components/PageBreadcrumb.vue';
import AppFooter from '../../components/AppFooter.vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-vue-next';
import ErrorMessage from '../../components/ErrorMessage.vue';
import { ChevronDown } from 'lucide-vue-next';
import ActionMenu from '@/components/ActionMenu.vue';
import _ from 'lodash';
import { toast } from '@/composables/useToast';
import { adjustControlConditionalValues, adjustControlConditionalVisibility, filterUserConfigurableConfigs } from '@/utils/utils';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { getConnectorIcon } from '@/utils/constants';

const props = defineProps({
    connector: {
        type: String,
        default: null
    },
    cloneConfig: {
        type: Object,
        default: null
    }
});
let selectedTitle = ref(null);
const destinations = useDestinationsStore();
const router = useRouter();
const { setDynamicLabel, clearDynamicLabel } = useBreadcrumbs();
const connection = ref({});
const connectionName = ref('');
let formError = ref('');
let loading = ref(false);
let loadingList = ref(false);
let allDestinationConnectors = ref([]);
let advancedMode = ref(false);
const hidePromptTemporary = ref(false);

const hideLateralDescriptionValue = ref(true);

onBeforeRouteLeave((to, from, next) => {
    if (loadingList.value) {
        return next();
    }

    if (hidePromptTemporary.value) {
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You may have unsaved changes.');

    if (answer) {
        return next();
    } else {
        return next(false);
    }
});

const handleResize = () => {
    hideLateralDescriptionValue.value = window.innerWidth <= 1024;
};

onMounted(() => {
    handleResize();
});

window.addEventListener('resize', handleResize);

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    clearDynamicLabel('new-destination');
});

onMounted(async () => {
    try {
        loadingList.value = true;
        allDestinationConnectors.value = await getDestinationsConnectors();

        selectedTitle.value = allDestinationConnectors.value?.find((conn) => conn.connector == props.connector)?.name;

        // Update breadcrumb with connector name
        if (selectedTitle.value) {
            setDynamicLabel('new-destination', `Add Destination - ${selectedTitle.value}`);
        }
    } finally {
        loadingList.value = false;
    }

    watch(
        props,
        async ({ connector }) => {
            loadingList.value = true;
            if (connector && !destinationConnectorConfig.value) await destinations.fetchConfiguration(connector);
            loadingList.value = false;
        },
        { immediate: true }
    );
});

const onSaveDestination = async () => {
    loading.value = true;
    hidePromptTemporary.value = true;
    const config = Object.values(connection.value).reduce((payload, { name, value }) => {
        return {
            ...payload,
            [name]: value
        };
    }, {});

    try {
        const data = await destinations.create({
            name: connectionName.value,
            connector: props.connector,
            config
        });

        toast.success('Destination created successfully.');
        if (data?.id) {
            // Navigate with clean state - no query parameters that might interfere
            router.push({
                name: 'destination-details',
                params: { id: data.id },
                query: {} // Explicitly clean query to prevent filter state conflicts
            });
        } else {
            router.push({ name: 'destinations-list' }); // Navigate to destinations list if no ID is returned
        }
    } catch (err) {
        formError.value = err;
        hidePromptTemporary.value = false;
    } finally {
        loading.value = false;
    }
};

const onFieldChanged = ({ value, valid, name }) => {
    const control = destinations.configurations[props.connector].find((x) => x.name == name);

    if (control) {
        control.value.current_value = value;
        control.value.valid = valid;
    }

    connection.value[name] = {
        value,
        valid,
        name
    };
};

const destinationConnectorConfig = computed(() => {
    const configurations = destinations.configurations[props.connector];
    if (!configurations) return null;

    let connectorConfig = _.cloneDeep(configurations);

    if (props.cloneConfig && connectorConfig) {
        const cloneConfig = JSON.parse(props.cloneConfig);

        connectorConfig = _.map(connectorConfig, (configItem) => {
            if ((configItem.encrypt || configItem.not_clonable) && props.cloneConfig) {
                if (configItem.not_clonable) {
                    configItem.value.default = undefined;
                }

                return configItem;
            }

            const clonedConfigValue = cloneConfig[configItem.name];
            if (_.isNil(clonedConfigValue)) return configItem;

            return {
                ...configItem,
                value: {
                    ...configItem.value,
                    default: clonedConfigValue
                }
            };
        });
    }

    return connectorConfig;
});

const visibleBasicConnectorConfigControls = computed(() => {
    const filteredConfigs = filterUserConfigurableConfigs(destinationConnectorConfig.value);

    const adjustedControls = _.chain(adjustControlConditionalValues(filteredConfigs))
        .filter((x) => !x.display_advanced)
        .orderBy('order_of_display', 'asc')
        .map((control) => adjustControlConditionalVisibility(control, filteredConfigs)?.control || null)
        .compact()
        .value();

    let controlsToRecheck = adjustedControls.filter((c) => c.conditions?.length);

    if (controlsToRecheck.length) {
        controlsToRecheck.map((oldControl) => {
            return onFieldChanged({
                value: oldControl.value.current_value || oldControl.value.default,
                valid: oldControl.value.valid || false,
                name: oldControl.name
            });
        });
    }

    return adjustedControls;
});

const visibleAdvancedConnectorConfigControls = computed(() => {
    const filteredConfigs = filterUserConfigurableConfigs(destinationConnectorConfig.value);

    const adjustedControls = _.chain(adjustControlConditionalValues(filteredConfigs))
        .filter((x) => x.display_advanced)
        .orderBy('order_of_display', 'asc')
        .map((control) => adjustControlConditionalVisibility(control, filteredConfigs)?.control || null)
        .compact()
        .value();

    let controlsToRecheck = adjustedControls.filter((c) => c.conditions?.length);

    if (controlsToRecheck.length) {
        controlsToRecheck.map((oldControl) => {
            return onFieldChanged({
                value: oldControl.value.current_value || oldControl.value.default,
                valid: oldControl.value.valid || false,
                name: oldControl.name
            });
        });
    }

    return adjustedControls;
});

const enableSave = computed(() => {
    if (props.connector && destinationConnectorConfig && connectionName.value.length > 0) {
        const filteredConfigs = filterUserConfigurableConfigs(destinationConnectorConfig.value);
        return filteredConfigs.reduce((valid, { name, display_in_ui }) => {
            return display_in_ui ? valid && connection.value[name] && connection.value[name].valid : true;
        }, true);
    } else {
        return false;
    }
});

async function getDestinationsConnectors() {
    var results = [];

    const values = await destinations.fetchConnectors();

    Object.values(values || {})
        .flat()
        .forEach((value) => {
            const item = { name: value.display_name, connector: value.connector };
            results.push(item);
        });

    return _.orderBy(results, 'name', ['asc']);
}

const getConnectorType = (destinationConnector) => {
    const destinationConnectors = destinations.destinationConnectors;
    const connector = Object.values(destinationConnectors)
        .flat()
        .find((x) => x.connector == destinationConnector);

    if (!connector?.status) {
        return '';
    }

    const arr = connector?.status?.split(' ');

    for (var i = 0; i < arr.length; i++) {
        arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
    }

    const firstLetterUpper = arr.join(' ');

    return firstLetterUpper == 'Beta' ? firstLetterUpper : '';
};

const getConnectorTypeColor = (connector) => {
    const destinationType = getConnectorType(connector);

    let result = '#F3F4F6';
    switch (destinationType) {
        case 'Beta':
            result = 'rgb(254 249 195)';
            break;
        default:
            break;
    }
    return result;
};

const getConnectorTypeDarkClass = (connector) => {
    const destinationType = getConnectorType(connector);

    switch (destinationType) {
        case 'Beta':
            return 'dark:!bg-yellow-900/40 dark:!text-yellow-200 dark:!border-yellow-800/50';
        default:
            return 'dark:!bg-gray-700/60 dark:!text-gray-200 dark:!border-gray-600/50';
    }
};

function getClass(control, advanced = false) {
    const spaceLeft = control.space_left || 0;

    let usedConnectors = advanced ? visibleAdvancedConnectorConfigControls.value : visibleBasicConnectorConfigControls.value;
    usedConnectors = _.orderBy(
        usedConnectors.filter((x) => x.display_in_ui),
        'order_of_display',
        ['asc']
    );

    const currentControlIndex = usedConnectors.findIndex((x) => x.name === control.name);
    const nextControl = usedConnectors[currentControlIndex + 1];

    const nextControlSpaceLeft = nextControl ? nextControl.space_left : undefined;

    // Predefined mapping for padding-left classes.
    // Extend this mapping if you expect more spacing values.
    const paddingLeftMapping = {
        0: 'pl-0',
        1: 'pl-5',
        2: 'pl-10',
        3: 'pl-15',
        4: 'pl-20',
        5: 'pl-25',
        6: 'pl-30'
    };

    const paddingLeftClass = paddingLeftMapping[spaceLeft] || '';

    let marginBottomClass = 'mb-6'; // Default margin for same level.

    if (nextControlSpaceLeft === undefined) {
        // Last control: standard margin.
        marginBottomClass = 'mb-6';
    } else if (nextControlSpaceLeft > spaceLeft) {
        // Next control is a child: smaller margin.
        marginBottomClass = 'mb-2';
    } else if (nextControlSpaceLeft === spaceLeft) {
        // Next control is at the same level: standard margin.
        marginBottomClass = 'mb-6';
    } else if (nextControlSpaceLeft < spaceLeft) {
        // Next control is at a higher level: larger margin.
        marginBottomClass = 'mb-8';
    }

    return `${paddingLeftClass} ${marginBottomClass}`.trim();
}
</script>

<template>
    <div>
        <div class="page-header">
            <PageBreadcrumb />
        </div>
        <div>
            <!-- Loading State with Skeleton -->
            <template v-if="loadingList">
                <!-- Connector Header Skeleton -->
                <div class="flex justify-between items-center mb-3">
                    <div class="flex flex-row gap-3 items-center">
                        <SkeletonLoader type="circle" class="h-12 w-12" :loading="true" />
                        <SkeletonLoader class="h-6 w-48" :loading="true" />
                    </div>
                    <SkeletonLoader class="h-8 w-16" :loading="true" />
                </div>

                <!-- Form Card Skeleton -->
                <div class="flex flex-row justify-center w-full">
                    <div class="w-full">
                        <Card>
                            <CardContent>
                                <div class="grid grid-cols-1 sm:grid-cols-1 sm:gap-x-8 mt-5">
                                    <!-- Name Field Skeleton -->
                                    <div class="w-full flex flex-col items-start relative mb-3">
                                        <SkeletonLoader class="h-4 w-12 mb-2" :loading="true" />
                                        <SkeletonLoader class="h-10 w-full" :loading="true" />
                                    </div>

                                    <!-- Dynamic Fields Skeleton -->
                                    <div v-for="n in 6" :key="`field-${n}`" class="mb-6">
                                        <SkeletonLoader class="h-4 w-24 mb-2" :loading="true" />
                                        <SkeletonLoader class="h-10 w-full" :loading="true" />
                                    </div>

                                    <!-- Advanced Section Skeleton -->
                                    <div class="mt-4">
                                        <SkeletonLoader class="h-12 w-full rounded-lg" :loading="true" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </template>

            <!-- Actual Content -->
            <template v-else>
                <div v-if="selectedTitle && props.connector" class="flex justify-between items-center mb-3">
                    <div class="flex flex-row gap-3 items-center">
                        <Avatar class="h-12 w-12 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                            <AvatarImage
                                :src="getConnectorIcon(props.connector) || `/icons/${props.connector}.svg`"
                                :alt="`${props.connector} icon`"
                                class="object-contain p-1" />
                            <AvatarFallback
                                class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground font-semibold text-lg rounded-md">
                                {{ props.connector?.charAt(0).toUpperCase() }}
                            </AvatarFallback>
                        </Avatar>
                        <span class="text-lg font-medium text-foreground">{{ selectedTitle }}</span>
                    </div>
                    <div v-if="getConnectorType(props.connector)">
                        <Badge
                            variant="default"
                            class="uppercase px-4 py-2"
                            :style="`background: ${getConnectorTypeColor(props.connector)};`"
                            :class="getConnectorTypeDarkClass(props.connector)">
                            {{ getConnectorType(props.connector) }}
                        </Badge>
                    </div>
                </div>

                <div v-if="destinationConnectorConfig" class="flex flex-row justify-center w-full">
                    <div class="w-full">
                        <Card>
                            <CardContent class="p-0">
                                <div class="pr-2">
                                    <div class="px-6">
                                        <form
                                            class="grid grid-cols-1 sm:grid-cols-1 sm:gap-x-8 mt-5"
                                            action="#"
                                            method="POST"
                                            autocomplete="new-password">
                                            <div class="w-full flex flex-col items-start relative my-1 mb-5">
                                                <div class="w-full mt-1 flex">
                                                    <div :class="hideLateralDescriptionValue ? 'w-full' : 'w-1/2 pr-2'">
                                                        <Label for="name" class="mb-2">Name</Label>
                                                        <div class="flex items-center relative">
                                                            <Input
                                                                id="name"
                                                                v-model="connectionName"
                                                                name="name"
                                                                type="text"
                                                                autocomplete="new-password"
                                                                required
                                                                v-sanitize
                                                                v-sanitize-csv
                                                                :class="hideLateralDescriptionValue ? 'pr-8' : ''" />
                                                            <Tooltip v-if="hideLateralDescriptionValue">
                                                                <TooltipTrigger asChild>
                                                                    <span
                                                                        class="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center">
                                                                        <Info class="h-4 w-4 text-muted-foreground" />
                                                                    </span>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="right" class="whitespace-normal max-w-xs">
                                                                    <p class="text-xs">The name of the destination</p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                    <div v-if="!hideLateralDescriptionValue" class="w-1/2 pl-2">
                                                        <Alert
                                                            variant="default"
                                                            class="min-h-[calc(theme(spacing.10)+theme(spacing.6))] flex items-center p-3 text-xs border bg-muted/10">
                                                            <AlertDescription class="text-muted-foreground text-2xs"
                                                                >The name of the destination</AlertDescription
                                                            >
                                                        </Alert>
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                v-show="control.display_in_ui"
                                                v-for="(control, index) in visibleBasicConnectorConfigControls"
                                                :key="index">
                                                <DynamicField
                                                    :key="`${control.name}_${control.display_in_ui}`"
                                                    :config="control"
                                                    :input-class="getClass(control)"
                                                    :hide-lateral-description="hideLateralDescriptionValue"
                                                    @input="onFieldChanged" />
                                            </div>

                                            <div v-show="visibleAdvancedConnectorConfigControls?.filter((c) => c.display_in_ui).length > 0">
                                                <div
                                                    class="flex w-full items-center justify-between border px-4 py-3 font-medium transition-all hover:bg-accent cursor-pointer"
                                                    :class="advancedMode ? 'rounded-t-lg border-b-0' : 'rounded-lg'"
                                                    @click="advancedMode = !advancedMode">
                                                    Advanced
                                                    <ChevronDown
                                                        class="h-4 w-4 shrink-0 transition-transform duration-200"
                                                        :class="advancedMode ? 'rotate-180' : ''" />
                                                </div>
                                                <div v-show="advancedMode" class="border border-t-0 rounded-b-lg p-5">
                                                    <div class="grid grid-cols-1 sm:grid-cols-1 sm:gap-x-8">
                                                        <div
                                                            v-show="control.display_in_ui"
                                                            v-for="(control, index) in visibleAdvancedConnectorConfigControls"
                                                            :key="index">
                                                            <DynamicField
                                                                :key="`${control.name}_${control.display_in_ui}`"
                                                                :config="control"
                                                                :input-class="getClass(control, true)"
                                                                :hide-lateral-description="hideLateralDescriptionValue"
                                                                @input="onFieldChanged" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </template>
        </div>

        <!-- Footer will teleport to App.vue footer slot -->
        <AppFooter v-if="destinationConnectorConfig">
            <template #right>
                <div v-if="formError" class="flex flex-col text-md font-semibold items-end mr-10">
                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'create-destination'" />
                </div>
                <ActionMenu
                    variant="row"
                    :show-save="true"
                    :show-discard="false"
                    :disable-save="!enableSave || loading"
                    :loading="loading"
                    @save="onSaveDestination" />
            </template>
        </AppFooter>
    </div>
</template>

<style>
.arrow-left::before {
    content: '';
    position: absolute;
    left: -12px; /* Adjusted for the larger arrow */
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 18px solid transparent; /* increased for a taller arrow */
    border-bottom: 18px solid transparent; /* increased for a taller arrow */
    border-right: 12px solid #bbf7d0; /* adjusted to match the border color of the blue container */
}
</style>
